import {Component, Input, Output, EventEmitter, OnInit, OnDestroy, HostListener, ChangeDetectionStrategy, OnChanges, SimpleChanges, ChangeDetectorRef} from '@angular/core';
import {SnackService} from '../../../../services/snack/snack.service';
import {AuthService} from '../../../../services/auth/auth.service';
import {TranslateService} from '@ngx-translate/core';
import {CartEvent, EventLocationType, GiftTicketReminder, LegacyTicket, PriceType, TicketsRequest, TicketPrice, TicketPriceType, UnassignedTicket} from '../../../../generated/sales-terminal-client';
import {cloneDeep} from 'lodash-es';
import {GiftTicketService} from '../../../../services/giftticket/giftTicket.service';
import {ConfigureEmailDialogComponent} from '../../../../components/configure-email-dialog/configure-email-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {GroupTickets} from '../cart.constants';
import {EventDetailsDialogComponent} from '../../../../components/event-details-dialog/event-details-dialog.component';
import {EventService} from '../../../../services/event/event.service';
import {UtilService} from "../../../../services/util/util.service";
import {MatCheckboxChange} from '@angular/material/checkbox';
import {Observable, Subscription, tap} from 'rxjs';
import {PendingChangesComponent} from '../../../../guards/pendingChanges/pending-changes-component';
import {CartAdvancedInterface} from '../../../../services/cart/cart.interface';
import {CartSortedEvent, CartTypes, ChildSelectionState, SelectionMap} from '../../../../services/cart/cart-base.service';
import {Privileges} from '../../../admin/privileges/privileges.constants';
import {ConfirmDialogComponent} from '../../../../components/confirm-dialog/confirm-dialog.component';
import {PriceInCentsPipe} from '../../../../pipes/priceInCents/priceInCents.pipe';
import {TicketTypeService} from '../../../../services/ticket-type/ticket-type.service';
import {TO_EVENT_BO_VIEW} from '../../sales-terminal.constants';
import {RouteService} from '../../../../services/route/route.service';
import {MatDialogRef} from '@angular/material/dialog';
import {NumericInputType} from '../../../../components/forms/numeric-input/numeric-input.component';
import {CentrePropertyService} from "../../../../services/centre-property/centre-property.service";

export interface NavigationParameters {
  eventId: number;
  rememberBooking: boolean;
}
export interface CustomTicket extends LegacyTicket {
  customPrice?: string;
}

@Component({
  selector: 'app-cart-table',
  templateUrl: './cart-table.component.html',
  styleUrls: ['./cart-table.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CartTableComponent<Type> implements OnInit, OnDestroy, PendingChangesComponent, OnChanges {
  @Input() public service: CartAdvancedInterface<Type>;

  @Input() canSelect: boolean = true;

  private _bulkMode: boolean = false;
  private _bulkModeCallback: boolean = false;
  @Input()
  public set bulkMode(value: boolean) {
    this._bulkMode = value;
    if (this._bulkMode) {
      this._bulkModeCallback = true;
      this._bulkSelectionMap = cloneDeep(this.service.selectionMap);
      if (this.utilService.isMobile && this.areAllCollapsed) {
        this.toggleCollapseAll();
      }
    }
    if (!this._bulkMode && this._bulkModeCallback) {
      this.bulkChangeCallback();
    }
  }
  public get bulkMode(): boolean {
    return this._bulkMode;
  }

  @Input() public isLoading: boolean;
  @Input() public canEdit: boolean = true;
  @Input() public collapsible: boolean = true;
  @Input() public activeBookingMode: boolean;

  @Output() public navigateToEventView: EventEmitter<NavigationParameters> = new EventEmitter<NavigationParameters>();
  @Output() public toggleBulkMode: EventEmitter<any> = new EventEmitter<any>();

  public Privileges = Privileges;
  public GroupTickets = GroupTickets;
  public ChildSelectionState = ChildSelectionState;
  public CartTypes = CartTypes;
  public EventLocationType = EventLocationType;
  public ticketReminderEmailMap: Map<number, string> = new Map<number, string>();
  public excludeOwnFees: boolean;

  private _collapseMap: Record<string, boolean> = {};
  private _bulkSelectionMap: SelectionMap = {
    checked: ChildSelectionState.ALL,
    events: {}
  };
  private bulkRemovedTicketIds: number[] = [];
  private bulkRemovedTicketTypes: Record<string, Record<string, number>> = {}; // eventId & ticketsTypeUuid + count
  private _subs: Subscription[] = [];

  constructor(
    public authService: AuthService,
    public centrePropertyService: CentrePropertyService,
    public utilService: UtilService,
    public ticketTypeService: TicketTypeService,
    public changeDetector: ChangeDetectorRef,
    private dialog: MatDialog,
    private routeService: RouteService,
    private snackService: SnackService,
    private translateService: TranslateService,
    private giftTicketService: GiftTicketService,
    private eventService: EventService,
    private priceInCentsPipe: PriceInCentsPipe) {}

  public ngOnInit(): void {
    this.setupTicketReminderEmailMap();
    this.excludeOwnFees = this.centrePropertyService.isEnabled('exclude_own_fees');
    if (this.utilService.isMobile || (this.service.cartType !== CartTypes.SelfService && this.service.ticketCount > 50)) {
      this.toggleCollapseAll();
    }
    this._subs.push(this.service.markForCheck.subscribe(() => {
      this.changeDetector.markForCheck();
    }));
    this.ticketTypeService.initTicketTypeHandling(this.service, this.service.events, this.changeDetector);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.changeDetector.markForCheck();
  }

  public ngOnDestroy(): void {
    this.ticketTypeService.clearSubscriptions();
    this._subs.forEach(s => s.unsubscribe());
    this._subs = [];
  }

  private bulkChangeCallback(): void {
    this._bulkModeCallback = false;
    this.service.selectionMap = cloneDeep(this._bulkSelectionMap);
    if (this.bulkRemovedTicketIds?.length) {
      this.service.removeTickets(this.bulkRemovedTicketIds);
    }
    Object.keys(this.bulkRemovedTicketTypes).forEach(eventId => {
      const requestArray: TicketsRequest[] = [];
      Object.keys(this.bulkRemovedTicketTypes[eventId])?.forEach(typeUuid =>
        requestArray.push({ticketTypeUuid: typeUuid, quantity: this.bulkRemovedTicketTypes[eventId][typeUuid]*-1}));
      this.service.removeTicketTypes(+eventId, requestArray);
    });
    this.bulkRemovedTicketIds = [];
    this.bulkRemovedTicketTypes = {};
    this.service.updateSelectionMapStates(this.service.selectionMap, true);
  }

  public get hasAnyPromoCode(): boolean {
    return this.service.cart?.events?.filter(event => event.boEvent && !!event.promocode?.length)?.length > 0;
  }

  public isBulkTicketRemoved(ticketId: number): boolean {
    return this.bulkRemovedTicketIds.includes(ticketId);
  }
  public isBulkTicketTypeRemoved(ticketTypeUuid: string, eventId: number): boolean {
    return !!(this.bulkRemovedTicketTypes[eventId] && this.bulkRemovedTicketTypes[eventId][ticketTypeUuid]);
  }

  public canChangeTicketPrice(eventId: number, ticket: CustomTicket): boolean {
    return this.canChangePrice && this.isTicketSelected(ticket.id, eventId);
  }
  public get canChangePrice(): boolean {
    return this.canEdit && this.authService.checkPrivileges(Privileges.TICKET_CUSTOM_PRICE);
  }

  public areAllTicketsSelected(selectionState: ChildSelectionState = ChildSelectionState.ALL): boolean {
    return (this.bulkMode ? this._bulkSelectionMap : this.service.selectionMap).checked === selectionState;
  }
  public toggleAllTickets(checkboxEvent: MatCheckboxChange): void {
    if (this.bulkMode) {
      this.populateBulkSelectionMap(checkboxEvent.checked ? ChildSelectionState.ALL : ChildSelectionState.NONE);
    } else {
      this.service.populateSelectionMap(checkboxEvent.checked ? ChildSelectionState.ALL : ChildSelectionState.NONE);
    }
    this.service.updateSelectionMapStates(this.bulkMode ? this._bulkSelectionMap : this.service.selectionMap, !this.bulkMode);
  }

  public isEventSelected(eventId: number, selectionState: ChildSelectionState = ChildSelectionState.ALL): boolean {
    return (this.bulkMode ? this._bulkSelectionMap : this.service.selectionMap)?.events[eventId]?.checked === selectionState;
  }
  public toggleEvent(checkboxEvent: MatCheckboxChange, eventId: number): void {
    const event: CartSortedEvent = this.service.ticketEventMap.find(e => e.id === eventId);
    const selectionMap = this.bulkMode ? this._bulkSelectionMap : this.service.selectionMap;
    this.service.populateSelectionMapEvent(event, selectionMap, checkboxEvent.checked ? ChildSelectionState.ALL : ChildSelectionState.NONE);
    this.service.updateSelectionMapStates(selectionMap, !this.bulkMode);
  }

  public isPriceZoneSelected(eventId: number, priceTypeId?: number, priceClassId?: number, selectionState: ChildSelectionState = ChildSelectionState.ALL): boolean {
    return (this.bulkMode ? this._bulkSelectionMap : this.service.selectionMap)
      .events[eventId].priceZones[priceClassId + '_' + priceTypeId].checked === selectionState;
  }
  public togglePriceZone(checkboxEvent: MatCheckboxChange, eventId: number, priceTypeId?: number, priceClassId?: number): void {
    const selectionState = checkboxEvent.checked ? ChildSelectionState.ALL : ChildSelectionState.NONE;
    const selectionMap = this.bulkMode ? this._bulkSelectionMap : this.service.selectionMap;
    this.service.populateSelectionMapPriceZone(eventId, priceClassId + '_' + priceTypeId, selectionMap, selectionState);
    this.service.updateSelectionMapStates(selectionMap, !this.bulkMode);
  }

  public isPriceRowSelected(eventId: number, priceTypeId?: number, row?: string, selectionState: ChildSelectionState = ChildSelectionState.ALL): boolean {
    return (this.bulkMode ? this._bulkSelectionMap : this.service.selectionMap)
      .events[eventId].priceRows[row + '_' + priceTypeId].checked === selectionState;
  }
  public togglePriceRow(checkboxEvent: MatCheckboxChange, eventId: number, priceTypeId?: number, row?: string): void {
    const selectionState = checkboxEvent.checked ? ChildSelectionState.ALL : ChildSelectionState.NONE;
    const selectionMap = this.bulkMode ? this._bulkSelectionMap : this.service.selectionMap;
    this.service.populateSelectionMapPriceRow(eventId, row + '_' + priceTypeId, selectionMap, selectionState);
    this.service.updateSelectionMapStates(selectionMap, !this.bulkMode);
  }

  public isPriceTypeSelected(eventId: number, priceTypeId?: number, selectionState: ChildSelectionState = ChildSelectionState.ALL): boolean {
    return (this.bulkMode ? this._bulkSelectionMap : this.service.selectionMap)
      .events[eventId].priceTypes[priceTypeId].checked === selectionState;
  }
  public togglePriceType(checkboxEvent: MatCheckboxChange, eventId: number, priceTypeId?: number): void {
    const selectionState = checkboxEvent.checked ? ChildSelectionState.ALL : ChildSelectionState.NONE;
    const selectionMap = this.bulkMode ? this._bulkSelectionMap : this.service.selectionMap;
    this.service.populateSelectionMapPriceType(eventId, priceTypeId, selectionMap, selectionState);
    this.service.updateSelectionMapStates(selectionMap, !this.bulkMode);
  }

  public isTicketSelected(ticketId: number, eventId: number): boolean {
    return (this.bulkMode ? this._bulkSelectionMap : this.service.selectionMap).events[eventId].tickets[ticketId] === ChildSelectionState.ALL;
  }
  public toggleTicket(checkboxEvent: MatCheckboxChange, ticketId: number, eventId: number, priceTypeId?: number, priceClassId?: number, row?: string): void {
    const selectionState = checkboxEvent.checked ? ChildSelectionState.ALL : ChildSelectionState.NONE;
    const selectionMap = this.bulkMode ? this._bulkSelectionMap : this.service.selectionMap;
    if (priceTypeId) {
      if (priceClassId) {
        selectionMap.events[eventId].priceZones[priceClassId + '_' + priceTypeId].tickets[ticketId] = selectionState;
      }
      if (row) {
        if (row === 'none') {
          row = '';
        }
        selectionMap.events[eventId].priceRows[row + '_' + priceTypeId].tickets[ticketId] = selectionState;
      }
      selectionMap.events[eventId].priceTypes[priceTypeId].tickets[ticketId] = selectionState;
    }
    selectionMap.events[eventId].tickets[ticketId] = selectionState;
    this.service.updateSelectionMapStates(selectionMap, !this.bulkMode);
  }

  public isTicketTypeSelected(ticketTypeUuid: string, eventId: number): boolean {
    return (this.bulkMode ? this._bulkSelectionMap : this.service.selectionMap).events[eventId].ticketTypes[ticketTypeUuid] === ChildSelectionState.ALL;
  }
  public toggleTicketType(checkboxEvent: MatCheckboxChange, ticketTypeUuid: string, eventId: number): void {
    const selectionState = checkboxEvent.checked ? ChildSelectionState.ALL : ChildSelectionState.NONE;
    const selectionMap = this.bulkMode ? this._bulkSelectionMap : this.service.selectionMap;
    selectionMap.events[eventId].ticketTypes[ticketTypeUuid] = selectionState;
    this.service.updateSelectionMapStates(selectionMap, !this.bulkMode);
  }

  public get areAllCollapsed(): boolean {
    return !!this._collapseMap['all'];
  }
  public toggleCollapseAll(): void {
    this.startLoader();
    const allCollapsed: boolean = this.areAllCollapsed;
    this._collapseMap['all'] = !allCollapsed;
    this.service.events.forEach(event => {
      this._collapseMap['event_' + event.id] = !allCollapsed;
    });
    this.stopLoader();
  }

  public isEventCollapsed(eventId: number): boolean {
    return !!this._collapseMap['event_' + eventId];
  }
  public toggleEventCollapse(eventId: number): void {
    this.startLoader();
    this._collapseMap['event_' + eventId] = !this.isEventCollapsed(eventId);
    this._collapseMap['all'] = this.service.events.every(event => this._collapseMap['event_' + event.id]);
    this.stopLoader();
  }

  public isPriceZoneCollapsed(id: number): boolean {
    return !!this._collapseMap['priceZone_' + id];
  }
  public togglePriceZoneCollapse(id: number): void {
    this.startLoader();
    this._collapseMap['priceZone_' + id] = !this.isPriceZoneCollapsed(id);
    this.stopLoader();
  }

  public isPriceRowCollapsed(id: string): boolean {
    return !!this._collapseMap['priceRow_' + id];
  }
  public togglePriceRowCollapse(id: string): void {
    this.startLoader();
    this._collapseMap['priceRow_' + id] = !this.isPriceRowCollapsed(id);
    this.stopLoader();
  }

  public isPriceTypeCollapsed(id: number): boolean {
    return !!this._collapseMap['priceType_' + id];
  }
  public togglePriceTypeCollapse(id: number): void {
    this.startLoader();
    this._collapseMap['priceType_' + id] = !this.isPriceTypeCollapsed(id);
    this.stopLoader();
  }

  public openGiftTicketReminderModal(ticket: LegacyTicket): void {
    const dialogRef = this.dialog.open(ConfigureEmailDialogComponent, {
      width: '50%',
      data: {
        title: this.translateService.instant('general.api_gift_ticket_reminder_email_title'),
        description: this.translateService.instant('general.api_gift_ticket_reminder_email_description'),
        email: this.ticketReminderEmailMap.get(ticket.id),
        buttonOk: this.translateService.instant('general.api_configure_email_button_ok')
      }
    });
    dialogRef.afterClosed().subscribe((res) => {
      if (res) {
        this.ticketReminderEmailMap.set(ticket.id, res.email);
        const giftTicketReminder: GiftTicketReminder = {reminderEmail: this.ticketReminderEmailMap.get(ticket.id), ticketId: ticket.id};
        this.giftTicketService.updateGiftTickets(giftTicketReminder).subscribe(() => {
          this.snackService.showSuccess(this.translateService.instant('general.api_gift_ticket_reminder_update_success'));
          this.changeDetector.markForCheck();
        });
      }
    });
  }

  public hasGiftTicketReminderEnabled(event: CartEvent): boolean {
    return event.reminderDays > 0;
  }

  public openEventDetails(eventId: number): void {
    this.eventService.getEvent(eventId).subscribe((event) => {
      const ref = this.dialog.open(EventDetailsDialogComponent, {
        width: '60%',
        data: {
          event: event,
          displayNavigateToEvent: false
        }
      });
      ref.afterClosed().subscribe((action) => {
        if (action === TO_EVENT_BO_VIEW) {
          this.routeService.navigate('/app/back-office/events/view/' + event.eventId);
        }
      });
    });
  }

  private setupTicketReminderEmailMap(): void {
    this.service.events.forEach(event => {
      event.legacyTickets?.forEach(ticket => {
        if (ticket.reminderEmail) {
          this.ticketReminderEmailMap.set(ticket.id, ticket.reminderEmail);
        }
      })
    });
  }

  public populateBulkSelectionMap(selectionState: ChildSelectionState = ChildSelectionState.ALL): void {
    this._bulkSelectionMap = {checked: selectionState, events: {}};
    this.service.ticketEventMap.forEach(event => {
      this.service.populateSelectionMapEvent(event, this._bulkSelectionMap, selectionState);
    });
  }

  public removeTicket(ticketId: number): void {
    if (this.bulkMode) {
      this.bulkRemovedTicketIds.push(ticketId);
    } else {
      this.service.removeTickets([ticketId]);
    }
  }

  public removeTicketType(ticketTypeUuid: string, eventId: number, quantity: number): void {
    if (this.bulkMode) {
      if (!this.bulkRemovedTicketTypes[eventId]) {
        this.bulkRemovedTicketTypes[eventId] = {};
      }
      this.bulkRemovedTicketTypes[eventId][ticketTypeUuid] = quantity;
    } else {
      this.service.removeTicketTypes(eventId, [{ticketTypeUuid: ticketTypeUuid, quantity: quantity*-1}]);
    }
  }

  public removeEvent(eventId: number): void {
    const dialogRef: MatDialogRef<ConfirmDialogComponent> = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('general.api_cart_event_clear_confirmation_title'),
        description: this.translateService.instant('general.api_cart_event_clear_confirmation_description'),
        buttonCancel: this.translateService.instant('general.api_general_cancel'),
        buttonOk: this.translateService.instant('general.api_cart_event_clear')
      }
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const chosenEvent: CartEvent = this.service.events.find(event => event.id === eventId);
        if (chosenEvent) {
          if (chosenEvent.boEvent) {
            const ticketsRequest: TicketsRequest[] = chosenEvent.unassignedTickets.map(type => ({ticketTypeUuid: type.ticketTypeUuid, quantity: type.quantity*-1}));
            if (ticketsRequest.length) {
              this.service.removeTicketTypes(eventId, ticketsRequest);
            } else if (chosenEvent.promocode) {
              this.removePromocode(eventId);
            }
          } else {
            this.service.removeTickets(chosenEvent.legacyTickets.map((t: LegacyTicket) => t.id));
          }
        }
      }
    });
  }

  public getAvailableTicketPrices(event: CartSortedEvent, ticket: CustomTicket): PriceType[] {
    return event.availablePriceMap[ticket.sectorId + '_' + ticket.priceClassId] || [];
  }

  public updatePriceTypesForGroup(eventId: number, priceTypeId: number, tickets: CustomTicket[]): void {
    if (priceTypeId && tickets?.length) {
      const request: TicketPriceType[] = [];
      tickets.forEach(ticket => request.push({id: ticket.id, priceTypeId: priceTypeId}));
      this.service.updateTicketPriceTypes(eventId, request).subscribe();
    }
  }
  public updateCustomPriceForGroup(eventId: number, price: string, tickets: CustomTicket[]): void {
    if (Number(price) >= 0 && tickets?.length) {
      const request: TicketPrice[] = [];
      tickets.forEach(ticket => request.push({id: ticket.id, price: this.priceInCentsPipe.transform(Number(price))}));
      this.service.updateTicketPrice(eventId, request).subscribe();
    }
  }

  public changeTicketTypeCount(ticketTypeUuid: string, eventId: number, count?: number): void {
    this.ticketTypeService.changeTicketTypeCount(ticketTypeUuid, eventId, count);
    this.changeDetector.markForCheck();
  }
  public getTicketTypeMax(id: string, eventId: number): number {
    return this.service.calculateMaxTickets(this.ticketTypeService.getTicketTypeInitialCount(id) + this.ticketTypeService.getTicketTypeAvailable(id, eventId));
  }

  public removePromocode(eventId: number): void {
    this.service.removeSelfServicePromocode(eventId).pipe(
      tap({finalize: () => this.service.retryCart(false, eventId)})
    ).subscribe();
  }

  private startLoader(): void {
    this.isLoading = true;
    this.changeDetector.markForCheck();
  }
  private stopLoader(): void {
    this.isLoading = false;
    this.changeDetector.markForCheck();
  }

  @HostListener('window:beforeunload')
  public canDeactivate(): Observable<boolean> | boolean {
    return !this.bulkMode || (this._bulkSelectionMap.checked !== ChildSelectionState.ALL && !this.bulkRemovedTicketIds.length && !Object.keys(this.bulkRemovedTicketTypes).length);
  }
  public get pendingChangesDescription(): string {
    return this.translateService.instant('general.api_bulk_mode_description_unsaved_changes');
  }
  public saveAndContinue(): void {
    this.toggleBulkMode.emit();
  }

  protected readonly NumericInputType = NumericInputType;
}
